import { create } from 'zustand'
import { CreativeService } from '../../../openapi/services/CreativeService'
import type { Composition } from '../composition/types'
import type { RubricDimensions, RubricVerdict } from '../process/creative-process.types'

/** How a version checkpoint came to be (mirrors the server `CreativeVersionKind`). */
export type CreativeVersionKind = 'generate' | 'ai' | 'manual' | 'restore'

/** A lightweight version-journey entry for the timeline (no heavy composition payload). */
export interface CreativeVersionSummary {
  seq: number
  kind: CreativeVersionKind
  label?: string | null
  feedback?: string | null
  score?: number | null
  verdict?: RubricVerdict | null
  dimensions?: RubricDimensions | null
  masterAttachmentId?: string | null
  createdAt: string
}

/** One version's full restorable document, loaded on demand for preview/restore. */
export interface CreativeVersionDetail {
  version: CreativeVersionSummary
  composition: Composition | null
  content: Record<string, unknown> | null
}

/**
 * The versions API — thin wrappers over the generated CreativeService that map the
 * loosely-typed OpenAPI DTOs (kind: string, nullable-as-optional) into the store's
 * typed view consumed by the UI.
 */
const VersionsApi = {
  list: async (creativeId: string): Promise<{ versions: CreativeVersionSummary[] }> => {
    const res = await CreativeService.creativeControllerListVersionsV1(creativeId)
    return { versions: (res.versions ?? []) as unknown as CreativeVersionSummary[] }
  },
  detail: async (creativeId: string, seq: number): Promise<CreativeVersionDetail> => {
    const res = await CreativeService.creativeControllerGetVersionV1(creativeId, seq)
    return {
      version: res.version as unknown as CreativeVersionSummary,
      composition: (res.composition ?? null) as Composition | null,
      content: res.content ?? null,
    }
  },
  restore: async (
    creativeId: string,
    seq: number
  ): Promise<{ versions: CreativeVersionSummary[] }> => {
    const res = await CreativeService.creativeControllerRestoreVersionV1(creativeId, seq)
    return { versions: (res.versions ?? []) as unknown as CreativeVersionSummary[] }
  },
}

interface VersionsState {
  versions: CreativeVersionSummary[]
  loading: boolean
  restoring: boolean
}

const EMPTY: VersionsState = { versions: [], loading: false, restoring: false }

interface VersionsStore {
  byCreative: Record<string, VersionsState>
  get: (creativeId: string) => VersionsState
  fetchVersions: (creativeId: string) => Promise<void>
  fetchDetail: (creativeId: string, seq: number) => Promise<CreativeVersionDetail | null>
  restore: (creativeId: string, seq: number) => Promise<CreativeVersionSummary[] | null>
}

export const useVersionsStore = create<VersionsStore>((set, get) => ({
  byCreative: {},

  get: creativeId => get().byCreative[creativeId] ?? EMPTY,

  fetchVersions: async creativeId => {
    set(state => ({
      byCreative: {
        ...state.byCreative,
        [creativeId]: { ...(state.byCreative[creativeId] ?? EMPTY), loading: true },
      },
    }))
    try {
      const { versions } = await VersionsApi.list(creativeId)
      set(state => ({
        byCreative: {
          ...state.byCreative,
          [creativeId]: { ...(state.byCreative[creativeId] ?? EMPTY), versions, loading: false },
        },
      }))
    } catch (err) {
      console.error('Failed to fetch creative versions:', err)
      set(state => ({
        byCreative: {
          ...state.byCreative,
          [creativeId]: { ...(state.byCreative[creativeId] ?? EMPTY), loading: false },
        },
      }))
    }
  },

  fetchDetail: async (creativeId, seq) => {
    try {
      return await VersionsApi.detail(creativeId, seq)
    } catch (err) {
      console.error('Failed to fetch version detail:', err)
      return null
    }
  },

  restore: async (creativeId, seq) => {
    set(state => ({
      byCreative: {
        ...state.byCreative,
        [creativeId]: { ...(state.byCreative[creativeId] ?? EMPTY), restoring: true },
      },
    }))
    try {
      const { versions } = await VersionsApi.restore(creativeId, seq)
      set(state => ({
        byCreative: {
          ...state.byCreative,
          [creativeId]: { ...(state.byCreative[creativeId] ?? EMPTY), versions, restoring: false },
        },
      }))
      return versions
    } catch (err) {
      console.error('Failed to restore version:', err)
      set(state => ({
        byCreative: {
          ...state.byCreative,
          [creativeId]: { ...(state.byCreative[creativeId] ?? EMPTY), restoring: false },
        },
      }))
      return null
    }
  },
}))
